import {
  returnIfBoundingBox2D,
  returnIfBoundingBox3D,
} from '@wandb/weave/common/types/media';
import {useMemo} from 'react';

import {DEFAULT_SCORE_GROUP_NAME} from '../common';
import {eachBoundingBox} from '../utils/eachBoundingBox';
import {type BoundingBoxData, useBoxData} from './useBoxData';

const updateRange = (
  scoreRanges: {[key: string]: {min: number; max: number}},
  scoreLabel: string,
  score: number
) => {
  const oldRange = scoreRanges[scoreLabel];
  let newRange;
  if (!oldRange) {
    newRange = {min: score, max: score};
  } else {
    newRange = {
      min: Math.min(oldRange.min, score),
      max: Math.max(oldRange.max, score),
    };
  }
  // NOTE: mutation of `scoreRanges` happens here:
  scoreRanges[scoreLabel] = newRange;
};

const getConfidenceRangesForAllMediaKeys = (
  allMediaKeys: string[] | undefined,
  boxData: BoundingBoxData | undefined
): Record<string, {min: number; max: number}> => {
  if (!allMediaKeys || !boxData) {
    return {};
  }

  const scoreRanges = {};

  // Loop through all the relevant boxes and their scores
  // Constructing a range value for each set
  for (const boundingBox of eachBoundingBox(allMediaKeys, boxData)) {
    const bbox3d = returnIfBoundingBox3D(boundingBox);
    const bbox2d = returnIfBoundingBox2D(boundingBox);

    // 2d bounding boxes support multiple independent categories of
    // scores, differentiated by label. 3d bounding boxes only supports 1
    // category, hardcoded with the label "score"
    if (bbox3d?.score) {
      updateRange(scoreRanges, DEFAULT_SCORE_GROUP_NAME, bbox3d.score);
    } else if (bbox2d?.scores) {
      for (const [label, score] of Object.entries(bbox2d.scores)) {
        updateRange(scoreRanges, label, score);
      }
    }
  }

  return scoreRanges;
};

/**
 * Given a panel id and a list of media keys to look at, return the slider
 * confidence ranges for each relevant labelled control.
 *
 * @param allMediaKeys
 * @param panelRefId
 */
export function useSliderRanges(
  allMediaKeys: string[] | undefined,
  panelRefId: string
) {
  const boxData = useBoxData(panelRefId);
  return useMemo(
    () => getConfidenceRangesForAllMediaKeys(allMediaKeys, boxData),
    [allMediaKeys, boxData]
  );
}
